import React from 'react';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

export interface VideoConfig {
  size: string;
  duration: number;
}

interface VideoConfigFormProps {
  config: VideoConfig;
  onChange: (config: VideoConfig) => void;
}

/**
 * VideoConfigForm - Configure video size and duration
 *
 * Size options: Sora-supported sizes only
 * - 720x1280 (Portrait), 1280x720 (Landscape)
 * - 1024x1792 (Tall Portrait), 1792x1024 (Wide Landscape)
 * Duration options: 4s, 8s, 12s
 */
export const VideoConfigForm: React.FC<VideoConfigFormProps> = ({ config, onChange }) => {
  const sizes = [
    { value: '1280x720', label: '1280x720 (Landscape)', description: 'Standard HD landscape' },
    { value: '720x1280', label: '720x1280 (Portrait)', description: 'Social media portrait' },
    { value: '1792x1024', label: '1792x1024 (Wide)', description: 'Cinematic wide format' },
    { value: '1024x1792', label: '1024x1792 (Tall)', description: 'Tall portrait format' }
  ];

  const durations = [
    { value: 4, label: '4 seconds', description: 'Quick snippet' },
    { value: 8, label: '8 seconds', description: 'Standard duration' },
    { value: 12, label: '12 seconds', description: 'Extended content' }
  ];

  return (
    <div className="space-y-4">
      {/* Video Size */}
      <div className="space-y-2">
        <Label htmlFor="video-size">Video Size</Label>
        <Select
          value={config.size}
          onValueChange={(size) => onChange({ ...config, size })}
        >
          <SelectTrigger id="video-size">
            <SelectValue placeholder="Select video size" />
          </SelectTrigger>
          <SelectContent>
            {sizes.map((size) => (
              <SelectItem key={size.value} value={size.value}>
                <div className="flex flex-col">
                  <span className="font-medium">{size.label}</span>
                  <span className="text-xs text-muted-foreground">{size.description}</span>
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Video Duration */}
      <div className="space-y-2">
        <Label htmlFor="video-duration">Duration</Label>
        <Select
          value={config.duration.toString()}
          onValueChange={(duration) => onChange({ ...config, duration: parseInt(duration) })}
        >
          <SelectTrigger id="video-duration">
            <SelectValue placeholder="Select duration" />
          </SelectTrigger>
          <SelectContent>
            {durations.map((duration) => (
              <SelectItem key={duration.value} value={duration.value.toString()}>
                <div className="flex flex-col">
                  <span className="font-medium">{duration.label}</span>
                  <span className="text-xs text-muted-foreground">{duration.description}</span>
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Info Display */}
      <div className="text-xs text-muted-foreground space-y-1 mt-4 p-3 bg-muted/50 rounded-lg">
        <p>
          <strong>Selected:</strong> {config.size} at {config.duration} seconds
        </p>
        <p>
          <strong>File size:</strong> ~{Math.round((config.duration * 2) / 10) * 10}MB estimated
        </p>
      </div>
    </div>
  );
};

export default VideoConfigForm;
